import { useOurCompany } from 'hooks/portal-settings.hook';
import { InvoiceStatusUrlParamsFilter } from 'entities/filters/invoice-status-url-params-filter';
import { ManagerUrlParamsFilter } from 'entities/filters/manager-url-params-filter';
import { DateUrlParamsFilter } from 'entities/filters/date-url-params-filter';
import { LegalEntityUrlParamsFilter } from 'entities/filters/legal-entity-url-params-filter';
import { FilterWrapper, ValueFormatterContext } from '../../../shared/ui/filter/filter-wrapper';
import { PeriodUrlParamsFilter } from 'entities/filters/period-url-params-filter';
import { SimpleSelectUrlParamsFilter } from 'entities/filters/simple-select-url-params-filter';
import { Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { INVOICE_STATUSES, InvoiceStatus } from '@sr/dto';
import { useManagersDropdownList } from 'features/user/user-picker/ui';
import { useLegalEntityLabel } from 'shared/hooks/use-entity-labels';

export type CounterpartyLegalEntityFilterConfig = {
  queryParamName: 'buyerLegalEntityId' | 'sellerLegalEntityId';
  companyIdFilter?: number;
  label: string;
};

export type LegalEntityFilterConfig = {
  companyIdFilter?: number;
  label: string;
};

export type InvoiceFilterProps = {
  withClosingPeriodFilter?: boolean;
  withInternetAdvertisingFilter?: boolean;
  counterpartyLegalEntityFilter?: CounterpartyLegalEntityFilterConfig;
  buyerLegalEntityFilter?: LegalEntityFilterConfig;
  sellerLegalEntityFilter?: LegalEntityFilterConfig;
};

const INTERNET_ADVERTISING_ITEMS = [
  { value: 'true', label: 'Да' },
  { value: 'false', label: 'Нет' }
];

export const InvoiceFilter = ({ withClosingPeriodFilter, withInternetAdvertisingFilter, counterpartyLegalEntityFilter, buyerLegalEntityFilter, sellerLegalEntityFilter }: InvoiceFilterProps) => {
  const [searchParams] = useSearchParams();
  const ourCompanyId = useOurCompany();
  const { items: managers } = useManagersDropdownList();
  const selectedLegalEntityId = searchParams.get('ourLegalEntityId');
  const legalEntityLabel = useLegalEntityLabel(selectedLegalEntityId);

  const legacySelectedId = counterpartyLegalEntityFilter ? searchParams.get(counterpartyLegalEntityFilter.queryParamName) : null;
  const legacyLabel = useLegalEntityLabel(legacySelectedId);

  const selectedBuyerLegalEntityId = buyerLegalEntityFilter ? searchParams.get('buyerLegalEntityId') : null;
  const buyerLegalEntityLabel = useLegalEntityLabel(selectedBuyerLegalEntityId);

  const selectedSellerLegalEntityId = sellerLegalEntityFilter ? searchParams.get('sellerLegalEntityId') : null;
  const sellerLegalEntityLabel = useLegalEntityLabel(selectedSellerLegalEntityId);

  const managerValueFormatter = ({ value }: ValueFormatterContext) => {
    const manager = managers.find((m) => String(m.id) === value);
    return manager ? `${manager.firstName} ${manager.secondName || ''}`.trim() : value;
  };

  const legalEntityValueFormatter = ({ value }: ValueFormatterContext) => {
    if (legalEntityLabel && selectedLegalEntityId === value) {
      return legalEntityLabel;
    }
    return value;
  };

  const statusValueFormatter = ({ value }: ValueFormatterContext) => {
    return INVOICE_STATUSES[Number(value) as InvoiceStatus] ?? value;
  };

  const internetAdvertisingValueFormatter = ({ value }: ValueFormatterContext) => {
    return INTERNET_ADVERTISING_ITEMS.find((item) => item.value === value)?.label ?? value;
  };

  const legacyCounterpartyValueFormatter = ({ value }: ValueFormatterContext) => {
    if (legacyLabel && legacySelectedId === value) return legacyLabel;
    return value;
  };

  const buyerValueFormatter = ({ value }: ValueFormatterContext) => {
    if (buyerLegalEntityLabel && selectedBuyerLegalEntityId === value) return buyerLegalEntityLabel;
    return value;
  };

  const sellerValueFormatter = ({ value }: ValueFormatterContext) => {
    if (sellerLegalEntityLabel && selectedSellerLegalEntityId === value) return sellerLegalEntityLabel;
    return value;
  };

  return (
    <FilterWrapper>
      <FilterWrapper.Item label="Менеджер" queryKeys="managerId" valueFormatter={managerValueFormatter}>
        <ManagerUrlParamsFilter queryParamName="managerId" />
      </FilterWrapper.Item>

      <FilterWrapper.Item
        label="Дата"
        queryKeys={['fromDate', 'toDate']}
        summaryFormatter={({ searchParams }) => {
          const fromDate = searchParams.get('fromDate');
          const toDate = searchParams.get('toDate');
          if (!fromDate && !toDate) return 'Дата';
          if (fromDate && toDate) return `Дата: ${fromDate} — ${toDate}`;
          return `Дата: ${fromDate || toDate}`;
        }}
      >
        <Stack spacing={1.5}>
          <DateUrlParamsFilter queryParamName={'fromDate'} label={'Дата с'} />
          <DateUrlParamsFilter queryParamName={'toDate'} label={'Дата по'} />
        </Stack>
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Статусы" queryKeys="statusIds" valueFormatter={statusValueFormatter}>
        <InvoiceStatusUrlParamsFilter queryParamName={'statusIds'} />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Наше юр. лицо" queryKeys="ourLegalEntityId" valueFormatter={legalEntityValueFormatter}>
        <LegalEntityUrlParamsFilter queryParamName={'ourLegalEntityId'} companyIdFilter={ourCompanyId} label={'Наше юр. лицо'} />
      </FilterWrapper.Item>

      {counterpartyLegalEntityFilter && (
        <FilterWrapper.Item
          label={counterpartyLegalEntityFilter.label}
          queryKeys={counterpartyLegalEntityFilter.queryParamName}
          valueFormatter={legacyCounterpartyValueFormatter}
        >
          <LegalEntityUrlParamsFilter
            queryParamName={counterpartyLegalEntityFilter.queryParamName}
            companyIdFilter={counterpartyLegalEntityFilter.companyIdFilter}
            label={counterpartyLegalEntityFilter.label}
          />
        </FilterWrapper.Item>
      )}

      {sellerLegalEntityFilter && (
        <FilterWrapper.Item label={sellerLegalEntityFilter.label} queryKeys="sellerLegalEntityId" valueFormatter={sellerValueFormatter}>
          <LegalEntityUrlParamsFilter queryParamName="sellerLegalEntityId" companyIdFilter={sellerLegalEntityFilter.companyIdFilter} label={sellerLegalEntityFilter.label} />
        </FilterWrapper.Item>
      )}

      {buyerLegalEntityFilter && (
        <FilterWrapper.Item label={buyerLegalEntityFilter.label} queryKeys="buyerLegalEntityId" valueFormatter={buyerValueFormatter}>
          <LegalEntityUrlParamsFilter queryParamName="buyerLegalEntityId" companyIdFilter={buyerLegalEntityFilter.companyIdFilter} label={buyerLegalEntityFilter.label} />
        </FilterWrapper.Item>
      )}

      {withInternetAdvertisingFilter && (
        <FilterWrapper.Item label="Реклама в интернете" queryKeys="isInternetAdvertising" valueFormatter={internetAdvertisingValueFormatter}>
          <SimpleSelectUrlParamsFilter<string> queryParamName="isInternetAdvertising" label="Реклама в интернете" items={INTERNET_ADVERTISING_ITEMS} />
        </FilterWrapper.Item>
      )}

      {withClosingPeriodFilter && (
        <FilterWrapper.Item
          label="Период закрытия"
          queryKeys={['closingMonth', 'closingYear']}
          summaryFormatter={({ searchParams }) => {
            const month = searchParams.get('closingMonth');
            const year = searchParams.get('closingYear');
            if (!month || !year) return 'Период закрытия';
            return `Период: ${month}.${year}`;
          }}
        >
          <PeriodUrlParamsFilter />
        </FilterWrapper.Item>
      )}
    </FilterWrapper>
  );
};
